import * as React from "react";
import { useFormContext } from "react-hook-form";
import Input from "../../atoms/input";
import Button from "../../atoms/button";
import SelectBox from "../../atoms/select";
import type { TournamentApplyInput } from "../../../types/tournament.types";
import { vw } from "../../../lib/utils/responsiveUtils";
import { REGION_OPTIONS } from "../../../lib/constants/region.constants";
import { cn } from "../../../lib/utils/cn";
import { useModalStore } from "../../../lib/store/useModalStore";

const REGION_SELECT_OPTIONS = [
  { label: "시/도 선택", value: "" },
  ...REGION_OPTIONS,
];

// 자격증 파일 최대 첨부 개수
const MAX_CERTIFICATE_FILES = 10;

interface Props {
  tournamentName?: string;
  /** 거주 지역 수집 대회 여부 */
  isCollectRegion?: boolean;
  /** 자격증 보유 여부 수집 대회 여부 */
  isCertificateAccept?: boolean;
  certificateMessage?: string | null;
  files: File[];
  setFiles: (files: File[]) => void;
  /** 얼럿 확인 후 이동할 입력 영역 ("region" | "certificate" | "email") */
  focusTarget?: string | null;
  /** focusTarget 처리 완료를 부모에 알림 (재요청 가능하도록 초기화) */
  onFocusHandled?: () => void;
}

export default function CompetitionInfoScreen({
  isCollectRegion = false,
  isCertificateAccept = false,
  certificateMessage,
  files,
  setFiles,
  focusTarget,
  onFocusHandled,
}: Props) {
  const { register, watch, setValue } = useFormContext<TournamentApplyInput>();
  const { openModal } = useModalStore();

  const region = watch("region") ?? "";
  const haveProCertificate = watch("haveProCertificate");

  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const regionRef = React.useRef<HTMLDivElement>(null);
  // 파일 첨부 버튼 영역으로 바로 이동시킨다.
  const certificateRef = React.useRef<HTMLDivElement>(null);
  const emailRef = React.useRef<HTMLDivElement>(null);
  const sectionRefs: Record<string, React.RefObject<HTMLElement | null>> = {
    region: regionRef,
    certificate: certificateRef,
    email: emailRef,
  };

  // 부모에서 focusTarget이 지정되면 해당 입력 영역으로 스크롤 이동
  React.useEffect(() => {
    if (!focusTarget) return;
    const section = sectionRefs[focusTarget]?.current;
    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "center" });
      section
        .querySelector<HTMLElement>("input, select")
        ?.focus({ preventScroll: true });
    }
    onFocusHandled?.();
  }, [focusTarget]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files: selected } = e.target;
    if (selected) {
      const merged = [...files, ...Array.from(selected)];
      // 최대 개수 초과 시 먼저 첨부한 순서로 잘라서 첨부하고 안내
      if (merged.length > MAX_CERTIFICATE_FILES) {
        setFiles(merged.slice(0, MAX_CERTIFICATE_FILES));
        openModal("ALERT_DIALOG", {
          title: "자격증 파일은 최대 10개까지 첨부할 수 있습니다.",
        });
      } else {
        setFiles(merged);
      }
    }
    // 같은 파일 재선택이 가능하도록 값 초기화
    e.target.value = "";
  };

  const previews = React.useMemo(
    () => files.map((f) => URL.createObjectURL(f)),
    [files],
  );

  React.useEffect(() => {
    return () => previews.forEach((url) => URL.revokeObjectURL(url));
  }, [previews]);

  return (
    <section>
      <h1 className="font-semibold  mb-[36px]">
        대회 참가에 필요한 추가 정보를 입력해 주세요.
      </h1>
      <div className="space-y-[20px]">
        {isCollectRegion && (
          <div className="space-y-[8px]" ref={regionRef}>
            <p className="text-base font-normal">
              지역&nbsp;<span className="text-[#ff3b30]">(필수)</span>
            </p>
            <SelectBox
              options={REGION_SELECT_OPTIONS}
              value={region}
              onChange={(e) =>
                setValue("region", e.target.value, { shouldValidate: true })
              }
              className={cn(
                "text-base w-full h-auto py-[12px] px-[16px]",
                region === "" ? "text-[#888]" : "",
              )}
            />
          </div>
        )}

        {isCertificateAccept && (
          <div>
            <p className="text-base font-normal mb-[8px]">
              자격증 보유 여부&nbsp;
              <span className="text-[#ff3b30]">(필수)</span>
            </p>

            <div className="flex-row gap-[10px]">
              <Button
                className={cn(
                  "w-full text-base",
                  haveProCertificate == false && "border border-black",
                )}
                variant={"white"}
                label="미보유"
                onClick={() => {
                  setValue("haveProCertificate", false);
                  // 미보유로 바꾸면 첨부한 자격증 파일은 제출되지 않으므로 비운다.
                  setFiles([]);
                }}
              />
              <Button
                className={cn(
                  "w-full text-base",
                  haveProCertificate == true && "border border-black",
                )}
                variant={"white"}
                label="보유"
                onClick={() => setValue("haveProCertificate", true)}
              />
            </div>
            {haveProCertificate == true && (
              <div className="mt-[20px]">
                <div className="space-y-[12px]">
                  <div className="space-y-[4px] font-normal text-base">
                    <h3 className="whitespace-pre-line break-keep">
                      파크골프 관련 자격증 파일 첨부
                    </h3>
                    <p className="text-[#8e8e93] whitespace-pre-line break-keep">
                      {certificateMessage}
                    </p>
                  </div>
                  <input
                    ref={inputRef}
                    type="file"
                    className="hidden"
                    accept="image/*"
                    multiple
                    onChange={handleChange}
                  />

                  <div ref={certificateRef}>
                    <Button
                      className="w-full text-base font-normal"
                      variant={
                        files.length >= MAX_CERTIFICATE_FILES
                          ? "disabled"
                          : "black"
                      }
                      disabled={files.length >= MAX_CERTIFICATE_FILES}
                      label={
                        files.length > 0
                          ? `자격증 파일 첨부 (${files.length}개)`
                          : "자격증 파일 첨부"
                      }
                      onClick={() => inputRef.current?.click()}
                    />
                  </div>

                  {previews.length > 0 && (
                    <div className="grid gap-[12px] grid-cols-3">
                      {previews.map((previewUrl, index) => (
                        <div
                          key={previewUrl}
                          className="relative aspect-square"
                        >
                          <div className="w-full h-full rounded-[10px] overflow-hidden border border-[#e6e6e6]">
                            <img
                              src={previewUrl}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div
                            className="absolute -top-[6px] -right-[6px] bg-[#888] rounded-full flex items-center justify-center cursor-pointer size-[24px]"
                            onClick={() =>
                              setFiles(files.filter((_, i) => i !== index))
                            }
                          >
                            <img
                              src="/icons/ic-close-white.webp"
                              style={{ width: vw(8), height: vw(8) }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="space-y-[8px]" ref={emailRef}>
          <p className="text-base font-normal">
            이메일&nbsp;<span className="text-[#8e8e93]">(선택)</span>
          </p>
          <Input
            className="text-[0.7rem] placeholder:text-[0.7rem]"
            placeholder="user@email.com"
            maxLength={100}
            {...register("email")}
          />
        </div>
      </div>
    </section>
  );
}
